@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_shared

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.example.project.enginekartei.EngineKarteiField
import org.example.project.enginekartei.EngineKarteiFieldType
import org.example.project.enginekartei.EngineKarteiScreen
import org.example.project.enginekartei.KarteiFieldOverride
import org.example.project.enginekartei.KarteiPageOverride
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.NodeScope
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private val anchorFields = listOf(
    EngineKarteiField("vorname", "Vorname"),
    EngineKarteiField("nachname", "Nachname")
)

private const val userverwaltungPage1Label = "Stammdaten"
private val page1Fields = listOf(
    EngineKarteiField("personalnummer", "Personalnummer"),
    EngineKarteiField("email", "E-Mail"),
    EngineKarteiField("kuerzel", "Kürzel"),
    EngineKarteiField("position", "Position"),
    EngineKarteiField("sprache", "Sprache")
)

private const val userverwaltungPage2Label = "Zugänge"
private val page2Fields = listOf(
    EngineKarteiField("zugangDesktop", "Zugang Desktop", type = EngineKarteiFieldType.CHECKBOX),
    EngineKarteiField("zugangMobil", "Zugang Mobil", type = EngineKarteiFieldType.CHECKBOX),
    EngineKarteiField("zugangCompanion", "Zugang Companion", type = EngineKarteiFieldType.CHECKBOX),
    EngineKarteiField("kontakteAktiv", "Kontakte", type = EngineKarteiFieldType.CHECKBOX)
)

private const val userverwaltungPage3Label = "Verwaltung"
private val page3Fields = listOf(
    EngineKarteiField("rolle", "Rolle"),
    EngineKarteiField("bereich", "Bereich"),
    EngineKarteiField("urlaubstageProJahr", "Urlaubstage pro Jahr"),
    EngineKarteiField("resturlaubVorjahr", "Resturlaub aus Vorjahr"),
    EngineKarteiField("datenschutzAkzeptiert", "Datenschutz akzeptiert", type = EngineKarteiFieldType.CHECKBOX),
    EngineKarteiField("passwortVerifiziert", "Passwort geändert", type = EngineKarteiFieldType.CHECKBOX)
)

val userverwaltungKarteiPages = listOf(
    All(id = "page_1", bezeichnung = userverwaltungPage1Label),
    All(id = "page_2", bezeichnung = userverwaltungPage2Label),
    All(id = "page_3", bezeichnung = "Netzwerk"),
    All(id = "page_4", bezeichnung = userverwaltungPage3Label)
)

private val userJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private val USER_TABLE_HEADERS = listOf(
    "ID",
    "Vorname",
    "Nachname",
    "Personalnummer",
    "E-Mail",
    "Kürzel",
    "Position",
    "Sprache",
    "Zugang Desktop",
    "Zugang Mobil",
    "Zugang Companion",
    "Rolle",
    "Bereich",
    "Urlaubstage/Jahr",
    "Resturlaub Vorjahr",
    "Datenschutz akzeptiert",
    "Passwort geändert"
)

private val NET_USER_TABLE_HEADERS = listOf(
    "ID",
    "Vorname",
    "Nachname",
    "E-Mail",
    "Kürzel",
    "Position",
    "Sprache",
    "Zugang Desktop",
    "Zugang Mobil",
    "Zugang Companion",
    "Datenschutz akzeptiert",
    "Passwort geändert"
)

private val USER_TABLE_TOGGLE_HEADERS = setOf(
    "Zugang Desktop",
    "Zugang Mobil",
    "Zugang Companion",
    "Datenschutz akzeptiert",
    "Passwort geändert"
)

private fun userToggle(value: Boolean): String = if (value) "✅" else "⬜"

private fun userToggleValue(value: String): Boolean =
    value.trim().equals("✅", ignoreCase = false) ||
        value.trim().equals("true", ignoreCase = true) ||
        value.trim() == "1" ||
        value.trim().equals("ja", ignoreCase = true)

private fun apiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun autoUserKuerzel(vorname: String, nachname: String): String =
    (vorname.trim().take(2) + nachname.trim().take(2)).uppercase()

private fun userKuerzelOrAuto(kuerzel: String, vorname: String, nachname: String): String =
    kuerzel.trim().ifBlank { autoUserKuerzel(vorname, nachname) }

private fun userTableRowWithAutoKuerzel(row: List<String>, art: String, changedColumn: String): List<String> {
    if (changedColumn != "Vorname" && changedColumn != "Nachname") return row
    val kuerzelIndex = if (isNet(art)) 4 else 5
    if (row.size <= kuerzelIndex) return row
    val vorname = row.getOrNull(1).orEmpty()
    val nachname = row.getOrNull(2).orEmpty()
    val auto = autoUserKuerzel(vorname, nachname)
    if (auto.isBlank()) return row
    val current = row.getOrNull(kuerzelIndex).orEmpty().trim()
    val oldPartial = when (changedColumn) {
        "Vorname" -> autoUserKuerzel("", nachname)
        "Nachname" -> autoUserKuerzel(vorname, "")
        else -> ""
    }
    val shouldUpdate = current.isBlank() ||
        current == oldPartial ||
        (current.length < auto.length && auto.startsWith(current))
    if (!shouldUpdate) return row
    return row.toMutableList().also { it[kuerzelIndex] = auto }
}

private fun buildUserSaveBody(state: All, id: String, firmenId: String): String =
    buildJsonObject {
        put("id", JsonPrimitive(id.ifBlank { "neu" }))
        put("firmenId", JsonPrimitive(firmenId))
        put("personalnummer", JsonPrimitive(state.personalnummer))
        put("vorname", JsonPrimitive(state.vorname))
        put("nachname", JsonPrimitive(state.nachname))
        put("email", JsonPrimitive(state.email))
        put("rolle", JsonPrimitive(state.rolle))
        put("position", JsonPrimitive(state.position))
        put("bereich", JsonPrimitive(state.bereich))
        put("zugangDesktop", JsonPrimitive(state.zugangDesktop))
        put("zugangMobil", JsonPrimitive(state.zugangMobil))
        put("zugangCompanion", JsonPrimitive(state.zugangCompanion))
        put("kontakteAktiv", JsonPrimitive(state.kontakteAktiv))
        put("zweiFaktorAktiv", JsonPrimitive(state.zweiFaktorAktiv))
        put("terminkalenderOnline", JsonPrimitive(state.terminkalenderOnline))
        put("datenschutzAkzeptiert", JsonPrimitive(state.datenschutzAkzeptiert))
        put("passwortVerifiziert", JsonPrimitive(state.passwortVerifiziert))
        put("terminkalenderIntern", JsonPrimitive(state.terminkalenderIntern))
        put("profilOnline", JsonPrimitive(state.profilOnline))
        put("profilIntern", JsonPrimitive(state.profilIntern))
        put("firma", JsonPrimitive(state.firma.ifBlank { currentFirma() }))
        put("kunde", JsonPrimitive(state.kunde.ifBlank { currentFirma() }))
        put("name", JsonPrimitive(state.name))
        put("kuerzel", JsonPrimitive(state.kuerzel))
        put("startbildschirm", JsonPrimitive(state.startbildschirm))
        put("sprache", JsonPrimitive(state.sprache.ifBlank { "de" }))
        put("urlaubstageProJahr", JsonPrimitive(state.urlaubstageProJahr))
        put("resturlaubVorjahr", JsonPrimitive(state.resturlaubVorjahr))
    }.toString()

private fun currentFirmenId(): String =
    LoginService.getUser()?.firmenId.orEmpty().trim()

private fun currentFirma(): String =
    LoginService.getUser()?.firma.orEmpty().trim()

private fun isNet(art: String): Boolean =
    art.uppercase() == NodeScope.NET

private fun userTableHeaders(art: String): List<String> =
    if (isNet(art)) NET_USER_TABLE_HEADERS else USER_TABLE_HEADERS

private fun normalizeNetPosition(value: String): String =
    if (value.trim().equals("Fahrer", ignoreCase = true)) "Fahrer" else "Verwaltung"

private fun normalizeNetUser(state: All): All {
    val position = normalizeNetPosition(state.position)
    return state.copy(
        position = position,
        sprache = state.sprache.ifBlank { "de" },
        zugangDesktop = position == "Verwaltung",
        zugangMobil = position == "Fahrer",
        rolle = if (position == "Fahrer") "Fahrer" else "",
        bereich = ""
    )
}

private suspend fun loadUsers(art: String): List<All> {
    val raw = runCatching {
        if (isNet(art)) {
            val firmenId = currentFirmenId()
            if (firmenId.isBlank()) "" else LumaticaApiManager.call("userverwaltung/company-users/${apiPart(firmenId)}")
        } else {
            LumaticaApiManager.call("userverwaltung")
        }
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { userJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun searchUsers(art: String, filters: Map<String, String>): List<All> {
    val raw = loadWithTabularFilters(
        "userverwaltung/search-tabular",
        filters,
        mapOf("mode" to if (isNet(art)) "net" else "sys")
    )
    return runCatching { userJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadRollen(): List<All> {
    val userId = LoginService.getId()
    val firmenId = currentFirmenId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("rollenverwaltung/user/${apiPart(userId)}/${apiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { userJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadBereiche(): List<All> {
    val userId = LoginService.getId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("bereich/entries/${apiPart(userId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { userJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadUser(art: String, userId: String): All? {
    if (userId.isBlank() || userId == "neu") return null
    val raw = runCatching {
        if (isNet(art)) {
            val firmenId = currentFirmenId()
            if (firmenId.isBlank()) "" else LumaticaApiManager.call("userverwaltung/company-user/${apiPart(firmenId)}/${apiPart(userId)}")
        } else {
            LumaticaApiManager.call("userverwaltung/${apiPart(userId)}")
        }
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { userJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun persistState(art: String, state: All): All? {
    val saveState = if (isNet(art)) normalizeNetUser(state) else state
    val id = saveState.id.trim()
    val api = if (isNet(art)) {
        val firmenId = currentFirmenId()
        if (firmenId.isBlank()) return null
        val body = buildUserSaveBody(saveState, id, firmenId)
        if (id.isBlank() || id == "neu") {
            "userverwaltung/company-create-json/${apiPart(firmenId)}/${apiPart(body)}"
        } else {
            "userverwaltung/company-save-json/${apiPart(firmenId)}/${apiPart(id)}/${apiPart(body)}"
        }
    } else if (id.isBlank() || id == "neu") {
        val body = buildUserSaveBody(saveState, "neu", saveState.firmenId.ifBlank { currentFirmenId() })
        "userverwaltung/save-json/${apiPart("neu")}/${apiPart(body)}"
    } else {
        val body = buildUserSaveBody(saveState, id, saveState.firmenId.ifBlank { currentFirmenId() })
        "userverwaltung/save-json/${apiPart(id)}/${apiPart(body)}"
    }
    val raw = runCatching { LumaticaApiManager.call(api) }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { userJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun deleteUserEntry(art: String, userId: String): Boolean {
    if (userId.isBlank() || userId == "neu") return false
    val raw = runCatching {
        if (isNet(art)) {
            val firmenId = currentFirmenId()
            if (firmenId.isBlank()) "" else LumaticaApiManager.call("userverwaltung/company-delete/${apiPart(firmenId)}/${apiPart(userId)}")
        } else {
            LumaticaApiManager.call("userverwaltung/delete/${apiPart(userId)}")
        }
    }.getOrDefault("")
    return raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
}

private suspend fun resetUserPassword(art: String, userId: String): All? {
    if (userId.isBlank() || userId == "neu") return null
    val raw = runCatching {
        if (isNet(art)) {
            val firmenId = currentFirmenId()
            if (firmenId.isBlank()) "" else LumaticaApiManager.call("userverwaltung/company-reset/${apiPart(firmenId)}/${apiPart(userId)}")
        } else {
            LumaticaApiManager.call("userverwaltung/reset/${apiPart(userId)}")
        }
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { userJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private fun sortUsers(items: List<All>): List<All> =
    items.sortedWith(
        compareBy<All>(
            { it.personalnummer.ifBlank { it.email }.lowercase() },
            { it.vorname.lowercase() },
            { it.nachname.lowercase() },
            { it.id.toIntOrNull() ?: Int.MAX_VALUE },
            { it.id }
        )
    )

private fun roleLabelForUser(roleId: String, rollen: List<All>): String =
    rollen.firstOrNull { it.id == roleId }?.name
        ?.ifBlank { rollen.firstOrNull { it.id == roleId }?.bezeichnung.orEmpty() }
        .orEmpty()
        .ifBlank { roleId }

private fun roleIdFromTableValue(value: String, rollen: List<All>): String {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return ""
    return rollen.firstOrNull {
        it.id.equals(trimmed, ignoreCase = true) ||
            it.name.equals(trimmed, ignoreCase = true) ||
            it.bezeichnung.equals(trimmed, ignoreCase = true)
    }?.id ?: trimmed
}

private fun rollenMitFahrer(rollen: List<All>): List<All> =
    if (rollen.any {
            it.id.equals("Fahrer", ignoreCase = true) ||
                it.name.equals("Fahrer", ignoreCase = true) ||
                it.bezeichnung.equals("Fahrer", ignoreCase = true)
        }
    ) {
        rollen
    } else {
        rollen + All(id = "Fahrer", name = "Fahrer", bezeichnung = "Fahrer")
    }

private fun sprachLabel(code: String): String =
    LocalizationManager.languages.firstOrNull { it.code.equals(code, ignoreCase = true) }?.name
        ?: LocalizationManager.languages.first().name

private fun sprachCode(value: String): String =
    LocalizationManager.languages.firstOrNull {
        it.code.equals(value.trim(), ignoreCase = true) ||
            it.name.equals(value.trim(), ignoreCase = true)
    }?.code ?: "de"

private fun bereichLabelForUser(bereichId: String, bereiche: List<All>): String =
    bereiche.firstOrNull { it.id == bereichId }?.name
        ?.ifBlank { bereiche.firstOrNull { it.id == bereichId }?.bezeichnung.orEmpty() }
        .orEmpty()
        .ifBlank { bereichId }

private fun bereichIdFromTableValue(value: String, bereiche: List<All>): String {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return ""
    return bereiche.firstOrNull {
        it.id.equals(trimmed, ignoreCase = true) ||
            it.name.equals(trimmed, ignoreCase = true) ||
            it.bezeichnung.equals(trimmed, ignoreCase = true)
    }?.id ?: trimmed
}

private fun userToTableRow(user: All, rollen: List<All>, bereiche: List<All>, art: String): List<String> =
    if (isNet(art)) {
        val netUser = normalizeNetUser(user)
        listOf(
            netUser.id,
            netUser.vorname,
            netUser.nachname,
            netUser.email,
            netUser.kuerzel,
            netUser.position,
            sprachLabel(netUser.sprache),
            userToggle(netUser.zugangDesktop),
            userToggle(netUser.zugangMobil),
            userToggle(netUser.zugangCompanion),
            userToggle(netUser.datenschutzAkzeptiert),
            userToggle(netUser.passwortVerifiziert)
        )
    } else listOf(
        user.id,
        user.vorname,
        user.nachname,
        user.personalnummer,
        user.email,
        user.kuerzel,
        user.position,
        sprachLabel(user.sprache),
        userToggle(user.zugangDesktop),
        userToggle(user.zugangMobil),
        userToggle(user.zugangCompanion),
        roleLabelForUser(user.rolle, rollen),
        bereichLabelForUser(user.bereich, bereiche),
        user.urlaubstageProJahr,
        user.resturlaubVorjahr,
        userToggle(user.datenschutzAkzeptiert),
        userToggle(user.passwortVerifiziert)
    )

private fun tableRowToUserState(
    row: List<String>,
    rollen: List<All>,
    bereiche: List<All>,
    art: String,
    existing: All? = null
): All {
    val rowId = row.getOrNull(0).orEmpty().trim()
    val resolvedId = if (existing != null) {
        rowId.takeUnless { it.isBlank() || it.startsWith("import_") } ?: ""
    } else {
        ""
    }
    val vorname = row.getOrNull(1).orEmpty().trim()
    val nachname = row.getOrNull(2).orEmpty().trim()
    val defaults = existing ?: All(
        firma = currentFirma(),
        kunde = currentFirma(),
        firmenId = currentFirmenId()
    )
    val hasUrlaubColumns = row.size >= 17
    if (isNet(art)) {
        return normalizeNetUser(
            defaults.copy(
                id = resolvedId,
                vorname = vorname,
                nachname = nachname,
                email = row.getOrNull(3).orEmpty().trim(),
                kuerzel = userKuerzelOrAuto(row.getOrNull(4).orEmpty(), vorname, nachname),
                position = row.getOrNull(5).orEmpty().trim(),
                sprache = sprachCode(row.getOrNull(6).orEmpty()),
                zugangCompanion = userToggleValue(row.getOrNull(9).orEmpty()),
                datenschutzAkzeptiert = userToggleValue(row.getOrNull(10).orEmpty()),
                passwortVerifiziert = userToggleValue(row.getOrNull(11).orEmpty()),
                name = listOf(vorname, nachname).filter { it.isNotBlank() }.joinToString(" ")
                    .ifBlank { defaults.name },
                firma = defaults.firma.ifBlank { currentFirma() },
                kunde = defaults.kunde.ifBlank { currentFirma() },
                firmenId = defaults.firmenId.ifBlank { currentFirmenId() }
            )
        )
    }
    return defaults.copy(
        id = resolvedId,
        vorname = vorname,
        nachname = nachname,
        personalnummer = row.getOrNull(3).orEmpty().trim(),
        email = row.getOrNull(4).orEmpty().trim(),
        kuerzel = userKuerzelOrAuto(row.getOrNull(5).orEmpty(), vorname, nachname),
        position = row.getOrNull(6).orEmpty().trim(),
        sprache = sprachCode(row.getOrNull(7).orEmpty()),
        zugangDesktop = userToggleValue(row.getOrNull(8).orEmpty()),
        zugangMobil = userToggleValue(row.getOrNull(9).orEmpty()),
        zugangCompanion = userToggleValue(row.getOrNull(10).orEmpty()),
        rolle = roleIdFromTableValue(row.getOrNull(11).orEmpty(), rollen),
        bereich = bereichIdFromTableValue(row.getOrNull(12).orEmpty(), bereiche),
        urlaubstageProJahr = if (hasUrlaubColumns) row.getOrNull(13).orEmpty().trim() else defaults.urlaubstageProJahr,
        resturlaubVorjahr = if (hasUrlaubColumns) row.getOrNull(14).orEmpty().trim() else defaults.resturlaubVorjahr,
        datenschutzAkzeptiert = userToggleValue(row.getOrNull(if (hasUrlaubColumns) 15 else 13).orEmpty()),
        passwortVerifiziert = userToggleValue(row.getOrNull(if (hasUrlaubColumns) 16 else 14).orEmpty()),
        name = listOf(vorname, nachname).filter { it.isNotBlank() }.joinToString(" ")
            .ifBlank { defaults.name },
        firma = defaults.firma.ifBlank { currentFirma() },
        kunde = defaults.kunde.ifBlank { currentFirma() },
        firmenId = defaults.firmenId.ifBlank { currentFirmenId() }
    )
}

@Composable
fun SHAREDUserverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var rollen by remember { mutableStateOf<List<All>>(emptyList()) }
    var bereiche by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedId by remember { mutableStateOf("") }
    var form by remember { mutableStateOf(newUserState(art)) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    val userVersion = LoginUserApi.version()

    suspend fun reload(keepId: String = selectedId) {
        loading = true
        users = sortUsers(loadUsers(art))
        rollen = loadRollen().sortedBy { it.name.ifBlank { it.id }.lowercase() }
        bereiche = loadBereiche().sortedBy { it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }.lowercase() }
        val nextId = keepId.ifBlank { users.firstOrNull()?.id.orEmpty() }
        selectedId = nextId
        form = users.firstOrNull { it.id == nextId } ?: newUserState(art)
        loading = false
    }

    fun updateText(field: String, value: String) {
        form = when (field) {
            "vorname" -> {
                val oldAuto = autoUserKuerzel(form.vorname, form.nachname)
                val next = form.copy(vorname = value)
                if (form.kuerzel.isBlank() || form.kuerzel == oldAuto) {
                    next.copy(kuerzel = autoUserKuerzel(next.vorname, next.nachname))
                } else next
            }
            "nachname" -> {
                val oldAuto = autoUserKuerzel(form.vorname, form.nachname)
                val next = form.copy(nachname = value)
                if (form.kuerzel.isBlank() || form.kuerzel == oldAuto) {
                    next.copy(kuerzel = autoUserKuerzel(next.vorname, next.nachname))
                } else next
            }
            "personalnummer" -> form.copy(personalnummer = value)
            "email" -> form.copy(email = value)
            "kuerzel" -> form.copy(kuerzel = value)
            "position" -> form.copy(position = value)
            "sprache" -> form.copy(sprache = value)
            "rolle" -> form.copy(rolle = value)
            "bereich" -> form.copy(bereich = value)
            "urlaubstageProJahr" -> form.copy(urlaubstageProJahr = value)
            "resturlaubVorjahr" -> form.copy(resturlaubVorjahr = value)
            else -> form
        }
        message = ""
    }

    fun updateToggle(field: String, value: Boolean) {
        form = when (field) {
            "zugangDesktop" -> form.copy(zugangDesktop = value)
            "zugangMobil" -> form.copy(zugangMobil = value)
            "zugangCompanion" -> form.copy(zugangCompanion = value)
            "kontakteAktiv" -> form.copy(kontakteAktiv = value)
            "profilOnline" -> form.copy(profilOnline = value)
            "profilIntern" -> form.copy(profilIntern = value)
            "terminkalenderOnline" -> form.copy(terminkalenderOnline = value)
            "terminkalenderIntern" -> form.copy(terminkalenderIntern = value)
            "zweiFaktorAktiv" -> form.copy(zweiFaktorAktiv = value)
            else -> form
        }
        message = ""
    }

    fun save() {
        if (saving) return
        saving = true
        message = ""
        scope.launch {
            val state = form.copy(
                firma = form.firma.ifBlank { currentFirma() },
                kunde = form.kunde.ifBlank { currentFirma() },
                firmenId = form.firmenId.ifBlank { currentFirmenId() },
                name = listOf(form.vorname, form.nachname).filter { it.isNotBlank() }.joinToString(" ").ifBlank { form.name }
            )
            val saved = persistState(art, state)
            if (saved == null) {
                message = "Benutzer konnte nicht gespeichert werden."
            } else {
                message = "Benutzer gespeichert."
                if (saved.id == LoginService.getId()) LoginService.updateUser { saved }
                reload(saved.id)
            }
            saving = false
        }
    }

    fun resetPassword() {
        val currentId = form.id
        if (currentId.isBlank() || currentId == "neu") return
        scope.launch {
            val reset = resetUserPassword(art, currentId)
            if (reset == null) {
                message = "Passwort konnte nicht zurückgesetzt werden."
                return@launch
            }
            message = "Passwort wurde zurückgesetzt."
            if (reset.id == LoginService.getId()) LoginService.updateUser { reset }
            reload(currentId)
        }
    }

    LaunchedEffect(art, currentFirmenId(), userVersion) { reload() }

    val title = listOf(form.vorname, form.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { form.email.ifBlank { "Benutzerverwaltung" } }

    Style(UserverwaltungStyles)
    Div({ classes(UserverwaltungStyles.page) }) {
        Div({ classes(UserverwaltungStyles.shell) }) {
            Div({ classes(UserverwaltungStyles.topbar) }) {
                Div({ classes(UserverwaltungStyles.header) }) {
                    Div({ classes(UserverwaltungStyles.iconBox) }) {
                        Img(src = "images/person.svg", attrs = { classes(UserverwaltungStyles.icon) })
                    }
                    Div({ classes(UserverwaltungStyles.headerText) }) {
                        H3({ classes(UserverwaltungStyles.title) }) { Text(title) }
                    }
                }
                Div({ classes(UserverwaltungStyles.actions) }) {
                    Div({ classes(UserverwaltungStyles.dropdownWrap) }) {
                        AppDropdown(
                            options = users.map { All(key = it.id, label = userFormLabel(it)) },
                            selectedValue = selectedId.ifBlank { null },
                            onChange = { id ->
                                selectedId = id
                                form = users.firstOrNull { it.id == id } ?: newUserState(art)
                                message = ""
                            },
                            placeholder = if (loading) "Benutzer werden geladen" else "Benutzer wählen",
                            appearance = AppDropdownAppearance.Surface,
                            attrs = {
                                style {
                                    width(100.percent)
                                    minHeight(36.px)
                                }
                            }
                        )
                    }
                    LumaticaButton(
                        label = "Neu",
                        onClick = {
                            selectedId = ""
                            form = newUserState(art)
                            message = ""
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 36
                    )
                    LumaticaButton(
                        label = if (saving) "Speichern..." else "Speichern",
                        onClick = ::save,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !saving,
                        heightPx = 36
                    )
                }
            }

            if (message.isNotBlank()) {
                Span({ classes(UserverwaltungStyles.message) }) { Text(message) }
            }

            Div({ classes(UserverwaltungStyles.content) }) {
                Div({ classes(UserverwaltungStyles.surface) }) {
                    UserFormSection(
                        title = "Stammdaten",
                        groups = listOf(
                            "Name" to listOf("vorname" to "Vorname", "nachname" to "Nachname"),
                            "Kontakt" to listOf("personalnummer" to "Personalnummer", "email" to "E-Mail"),
                            "Profil" to listOf("kuerzel" to "Kürzel", "position" to "Position")
                        ),
                        form = form,
                        onChange = ::updateText
                    )
                    UserAccessSection(form = form, onChange = ::updateToggle)
                    UserNetworkSection(form = form, onChange = ::updateToggle)
                    UserAdminSection(
                        art = art,
                        form = form,
                        rollen = rollen,
                        bereiche = bereiche,
                        onTextChange = ::updateText
                    )
                    UserSecuritySection(
                        form = form,
                        onToggleChange = ::updateToggle,
                        onResetPassword = ::resetPassword
                    )
                }
            }
        }
    }
}

@Composable
private fun SHAREDUserverwaltungKarteiAlt(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var rollen by remember { mutableStateOf<List<All>>(emptyList()) }
    var bereiche by remember { mutableStateOf<List<All>>(emptyList()) }
    val userVersion = LoginUserApi.version()

    suspend fun refreshUsers() {
        users = sortUsers(loadUsers(art))
    }

    suspend fun refreshRollen() {
        rollen = loadRollen().sortedBy { it.name.ifBlank { it.id }.lowercase() }
    }

    suspend fun refreshBereiche() {
        bereiche = loadBereiche().sortedBy { it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }.lowercase() }
    }

    LaunchedEffect(art, currentFirmenId(), userVersion) {
        refreshUsers()
        refreshRollen()
        refreshBereiche()
    }

    EngineKarteiScreen(
        entities = users,
        screenTitle = "Benutzererstellung",
        entityId = { it.id },
        entityLabel = { it.personalnummer.ifBlank { it.email.ifBlank { it.id.ifBlank { "neu" } } } },
        anchorFields = anchorFields,
        page1Label = userverwaltungPage1Label,
        page1Fields = page1Fields,
        page2Label = userverwaltungPage2Label,
        page2Fields = page2Fields,
        page3Label = userverwaltungPage3Label,
        page3Fields = page3Fields,
        fieldOverrides = mapOf(
            "sprache" to KarteiFieldOverride { value, onValue, _ ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(14.px)
                        padding(8.px, 8.px)
                    }
                }) {
                    Span({
                        style {
                            width(138.px)
                            minWidth(138.px)
                            color(Color("#8a8f9a"))
                            fontSize(14.px)
                        }
                    }) { Text("Sprache") }

                    Div({
                        style {
                            flex("1 1 auto")
                            minWidth(0.px)
                            minHeight(36.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                            padding(0.px)
                            boxSizing("border-box")
                        }
                    }) {
                        AppDropdown(
                            options = LocalizationManager.languages.map { language ->
                                All(
                                    key = language.code,
                                    label = language.name
                                )
                            },
                            selectedValue = value.ifBlank { "de" },
                            onChange = onValue,
                            placeholder = "Sprache wählen",
                            appearance = AppDropdownAppearance.Underline,
                            attrs = {
                                style {
                                    width(100.percent)
                                    minHeight(36.px)
                                    padding(0.px, 0.px, 6.px, 0.px)
                                }
                            }
                        )
                    }
                }
            },
            "rolle" to KarteiFieldOverride { value, onValue, _ ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(14.px)
                        padding(8.px, 8.px)
                    }
                }) {
                    Span({
                        style {
                            width(138.px)
                            minWidth(138.px)
                            color(Color("#8a8f9a"))
                            fontSize(14.px)
                        }
                    }) { Text("Rolle") }

                    Div({
                        style {
                            flex("1 1 auto")
                            minWidth(0.px)
                            minHeight(36.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                            padding(0.px)
                            boxSizing("border-box")
                        }
                    }) {
                        AppDropdown(
                            options = rollenMitFahrer(rollen).map { rolle ->
                                All(
                                    key = rolle.id,
                                    label = rolle.name.ifBlank { rolle.id }
                                )
                            },
                            selectedValue = value.ifBlank { null },
                            onChange = onValue,
                            placeholder = "Rolle wählen",
                            appearance = AppDropdownAppearance.Underline,
                            attrs = {
                                style {
                                    width(100.percent)
                                    minHeight(36.px)
                                    padding(0.px, 0.px, 6.px, 0.px)
                                }
                            }
                        )
                    }
                }
            },
            "bereich" to KarteiFieldOverride { value, onValue, _ ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(14.px)
                        padding(8.px, 8.px)
                    }
                }) {
                    Span({
                        style {
                            width(138.px)
                            minWidth(138.px)
                            color(Color("#8a8f9a"))
                            fontSize(14.px)
                        }
                    }) { Text("Bereich") }

                    Div({
                        style {
                            flex("1 1 auto")
                            minWidth(0.px)
                            minHeight(36.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                            padding(0.px)
                            boxSizing("border-box")
                        }
                    }) {
                        AppDropdown(
                            options = bereiche.map { bereich ->
                                All(
                                    key = bereich.id,
                                    label = bereich.name.ifBlank { bereich.bezeichnung.ifBlank { bereich.id } }
                                )
                            },
                            selectedValue = bereichIdFromTableValue(value, bereiche).ifBlank { null },
                            onChange = onValue,
                            placeholder = "Bereich wählen",
                            appearance = AppDropdownAppearance.Underline,
                            attrs = {
                                style {
                                    width(100.percent)
                                    minHeight(36.px)
                                    padding(0.px, 0.px, 6.px, 0.px)
                                }
                            }
                        )
                    }
                }
            }
        ),
        pageOverrides = mapOf(
            "page_3" to KarteiPageOverride { values, _, _ ->
                val currentId = values["id"].orEmpty()
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexStart)
                        marginTop(12.px)
                    }
                }) {
                    LumaticaButton(
                        label = "PW zurücksetzen",
                        onClick = {
                            scope.launch {
                                val reset = resetUserPassword(art, currentId)
                                if (reset == null) {
                                    AppDialogs.alert("Passwort konnte nicht zurückgesetzt werden.")
                                    return@launch
                                }
                                refreshUsers()
                                if (reset.id == LoginService.getId()) LoginService.updateUser { reset }
                                AppDialogs.alert("Passwort wurde zurückgesetzt.")
                            }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = currentId.isNotBlank() && currentId != "neu",
                        heightPx = 40
                    )
                }
            }
        ),
        screenKey = "UserVerwaltung",
        staticPhotoSrc = "images/person.svg",
        loadState = { id ->
            loadUser(art, id) ?: users.firstOrNull { it.id == id } ?: All(id = id)
        },
        saveState = { state ->
            val saved = persistState(art, state)
            if (saved == null) {
                AppDialogs.alert("Benutzer konnte nicht gespeichert werden.")
                return@EngineKarteiScreen state.copy(aktiv = false)
            }
            refreshUsers()
            if (saved.id == LoginService.getId()) LoginService.updateUser { saved }
            users.firstOrNull { it.id == saved.id } ?: saved
        },
        canCreate = true
    )
}

@Composable
private fun UserFormSection(
    title: String,
    groups: List<Pair<String, List<Pair<String, String>>>>,
    form: All,
    onChange: (String, String) -> Unit
) {
    Div({ classes(UserverwaltungStyles.sectionBlock) }) {
        Div({ classes(UserverwaltungStyles.sectionTitle) }) { Text(title) }
        groups.forEach { (groupTitle, fields) ->
            Div({ classes(UserverwaltungStyles.groupBlock) }) {
                Div({ classes(UserverwaltungStyles.groupTitle) }) { Text(groupTitle) }
                Div({ classes(UserverwaltungStyles.fieldGrid) }) {
                    fields.forEach { (field, label) ->
                        Div({ classes(UserverwaltungStyles.fieldRow) }) {
                            Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text(label) }
                            UserTextInput(value = userFieldValue(form, field), onChange = { onChange(field, it) })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun UserAccessSection(form: All, onChange: (String, Boolean) -> Unit) {
    Div({ classes(UserverwaltungStyles.sectionBlock) }) {
            Div({ classes(UserverwaltungStyles.sectionTitle) }) { Text("Zugänge") }
        Div({ classes(UserverwaltungStyles.toggleGrid) }) {
            UserToggleRow("Zugang Desktop", form.zugangDesktop) { onChange("zugangDesktop", it) }
            UserToggleRow("Zugang Mobil", form.zugangMobil) { onChange("zugangMobil", it) }
            UserToggleRow("Zugang Companion", form.zugangCompanion) { onChange("zugangCompanion", it) }
        }
    }
}

@Composable
private fun UserNetworkSection(form: All, onChange: (String, Boolean) -> Unit) {
    Div({ classes(UserverwaltungStyles.sectionBlock) }) {
        Div({ classes(UserverwaltungStyles.sectionTitle) }) { Text("Netzwerk") }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Kontakte") }
            Div({ classes(UserverwaltungStyles.toggleGrid) }) {
                UserToggleRow("Kontakte aktiv", form.kontakteAktiv) { onChange("kontakteAktiv", it) }
            }
        }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Profil / Chat") }
            Div({ classes(UserverwaltungStyles.toggleGrid) }) {
                UserToggleRow("Intern sichtbar", form.profilIntern) { onChange("profilIntern", it) }
                UserToggleRow("Online sichtbar", form.profilOnline) { onChange("profilOnline", it) }
            }
        }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Kalender") }
            Div({ classes(UserverwaltungStyles.toggleGrid) }) {
                UserToggleRow("Intern sichtbar", form.terminkalenderIntern) { onChange("terminkalenderIntern", it) }
                UserToggleRow("Online sichtbar", form.terminkalenderOnline) { onChange("terminkalenderOnline", it) }
            }
        }
    }
}

@Composable
private fun UserAdminSection(
    art: String,
    form: All,
    rollen: List<All>,
    bereiche: List<All>,
    onTextChange: (String, String) -> Unit
) {
    Div({ classes(UserverwaltungStyles.sectionBlock) }) {
        Div({ classes(UserverwaltungStyles.sectionTitle) }) { Text("Verwaltung") }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Berechtigung") }
            Div({ classes(UserverwaltungStyles.fieldGrid) }) {
                Div({ classes(UserverwaltungStyles.fieldRow) }) {
                    Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Sprache") }
                    UserDropdown(
                        options = LocalizationManager.languages.map { All(key = it.code, label = it.name) },
                        value = form.sprache.ifBlank { "de" },
                        placeholder = "Sprache wählen",
                        onChange = { onTextChange("sprache", it) }
                    )
                }
                if (isNet(art)) {
                    Div({ classes(UserverwaltungStyles.fieldRow) }) {
                        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Position") }
                        UserDropdown(
                            options = listOf("Fahrer", "Verwaltung").map { All(key = it, label = it) },
                            value = form.position.ifBlank { "Verwaltung" },
                            placeholder = "Position wählen",
                            onChange = { onTextChange("position", it) }
                        )
                    }
                } else {
                    Div({ classes(UserverwaltungStyles.fieldRow) }) {
                        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Rolle") }
                        UserDropdown(
                            options = rollenMitFahrer(rollen).map { All(key = it.id, label = it.name.ifBlank { it.id }) },
                            value = form.rolle,
                            placeholder = "Rolle wählen",
                            onChange = { onTextChange("rolle", it) }
                        )
                    }
                    Div({ classes(UserverwaltungStyles.fieldRow) }) {
                        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Bereich") }
                        UserDropdown(
                            options = bereiche.map { All(key = it.id, label = it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }) },
                            value = form.bereich,
                            placeholder = "Bereich wählen",
                            onChange = { onTextChange("bereich", it) }
                        )
                    }
                }
            }
        }
        if (!isNet(art)) {
            Div({ classes(UserverwaltungStyles.groupBlock) }) {
                Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Urlaub") }
                Div({ classes(UserverwaltungStyles.fieldGrid) }) {
                    Div({ classes(UserverwaltungStyles.fieldRow) }) {
                        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Urlaubstage pro Jahr") }
                        UserTextInput(form.urlaubstageProJahr) { onTextChange("urlaubstageProJahr", it) }
                    }
                    Div({ classes(UserverwaltungStyles.fieldRow) }) {
                        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text("Resturlaub aus Vorjahr") }
                        UserTextInput(form.resturlaubVorjahr) { onTextChange("resturlaubVorjahr", it) }
                    }
                }
            }
        }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Status") }
            Div({ classes(UserverwaltungStyles.toggleGrid) }) {
                UserToggleRow("Datenschutz akzeptiert", form.datenschutzAkzeptiert, editable = false) {}
                UserToggleRow("Passwort geändert", form.passwortVerifiziert, editable = false) {}
            }
        }
    }
}

@Composable
private fun UserSecuritySection(
    form: All,
    onToggleChange: (String, Boolean) -> Unit,
    onResetPassword: () -> Unit
) {
    Div({ classes(UserverwaltungStyles.sectionBlock) }) {
        Div({ classes(UserverwaltungStyles.sectionTitle) }) { Text("Sicherheit") }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Zwei-Faktor") }
            Div({ classes(UserverwaltungStyles.toggleGrid) }) {
                UserToggleRow("Zwei-Faktor aktiv", form.zweiFaktorAktiv) { onToggleChange("zweiFaktorAktiv", it) }
            }
        }
        Div({ classes(UserverwaltungStyles.groupBlock) }) {
            Div({ classes(UserverwaltungStyles.groupTitle) }) { Text("Passwort") }
            LumaticaButton(
                label = "PW zurücksetzen",
                onClick = onResetPassword,
                variant = LumaticaButtonVariant.Standard,
                enabled = form.id.isNotBlank() && form.id != "neu",
                heightPx = 36
            )
        }
    }
}

@Composable
private fun UserToggleRow(label: String, checked: Boolean, editable: Boolean = true, onChange: (Boolean) -> Unit) {
    Div({ classes(UserverwaltungStyles.toggleRow) }) {
        Span({ classes(UserverwaltungStyles.fieldLabel) }) { Text(label) }
        LumaticaCheckbox(checked = checked, onChange = onChange, fontSizePx = 20, enabled = editable)
    }
}

@Composable
private fun UserTextInput(value: String, onChange: (String) -> Unit) {
    Input(InputType.Text, attrs = {
        classes(UserverwaltungStyles.input)
        value(value)
        placeholder("")
        onInput { onChange(it.value) }
    })
}

@Composable
private fun UserDropdown(options: List<All>, value: String, placeholder: String, onChange: (String) -> Unit) {
    AppDropdown(
        options = options,
        selectedValue = value.ifBlank { null },
        onChange = onChange,
        placeholder = placeholder,
        appearance = AppDropdownAppearance.Surface,
        attrs = {
            style {
                width(100.percent)
                minHeight(34.px)
            }
        }
    )
}

private fun newUserState(art: String): All =
    normalizeNetUser(
        All(
            id = "neu",
            firma = currentFirma(),
            kunde = currentFirma(),
            firmenId = currentFirmenId(),
            position = if (isNet(art)) "Verwaltung" else "",
            sprache = "de",
            zugangDesktop = true,
            zugangMobil = !isNet(art)
        )
    ).takeIf { isNet(art) } ?: All(
        id = "neu",
        firma = currentFirma(),
        kunde = currentFirma(),
        firmenId = currentFirmenId(),
        sprache = "de",
        zugangDesktop = true,
        zugangMobil = true
    )

private fun userFormLabel(user: All): String =
    listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user.personalnummer.ifBlank { user.email.ifBlank { user.id.ifBlank { "neu" } } } }

private fun userFieldValue(user: All, field: String): String =
    when (field) {
        "vorname" -> user.vorname
        "nachname" -> user.nachname
        "personalnummer" -> user.personalnummer
        "email" -> user.email
        "kuerzel" -> user.kuerzel
        "position" -> user.position
        else -> ""
    }

private object UserverwaltungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        padding(18.px, 22.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val topbar by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
    }
    val header by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        flexShrink(0)
    }
    val iconBox by style {
        width(72.px)
        height(72.px)
        borderRadius(6.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(rgba(255, 255, 255, 0.07))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        boxSizing("border-box")
    }
    val icon by style {
        width(38.px)
        height(38.px)
        property("filter", "brightness(0) invert(1)")
        opacity(0.88)
    }
    val headerText by style {
        minWidth(0.px)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(8.px)
        flexWrap(FlexWrap.Nowrap)
        property("flex", "1 1 auto")
        minWidth(0.px)
    }
    val dropdownWrap by style {
        width(260.px)
        maxWidth(100.percent)
        property("flex", "0 1 260px")
    }
    val message by style {
        color(Color("#84e1a7"))
        fontSize(12.px)
        fontWeight("700")
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
    }
    val surface by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val sectionBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val sectionTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
    }
    val groupBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        paddingBottom(24.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        self + ":last-child" style {
            paddingBottom(0.px)
            property("border-bottom", "0")
        }
    }
    val groupTitle by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(13.px)
        fontWeight("800")
    }
    val fieldGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") }
        }
        media(mediaMaxWidth(640.px)) {
            self style { property("grid-template-columns", "1fr") }
        }
    }
    val fieldRow by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val fieldLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(10.px)
        fontWeight("800")
        property("text-transform", "uppercase")
        letterSpacing(0.px)
    }
    val input by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        property("outline", "none")
    }
    val toggleGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(760.px)) {
            self style { property("grid-template-columns", "1fr") }
        }
    }
    val toggleRow by style {
        minHeight(42.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        padding(0.px, 12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.05))
        boxSizing("border-box")
    }
}

@Composable
fun SHAREDUserverwaltungTabelle(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var rollen by remember { mutableStateOf<List<All>>(emptyList()) }
    var bereiche by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val userVersion = LoginUserApi.version()

    suspend fun refreshUsers() {
        users = letzteFilter?.let { sortUsers(searchUsers(art, it)) }.orEmpty()
    }

    suspend fun refreshRollen() {
        rollen = loadRollen().sortedBy { it.name.ifBlank { it.id }.lowercase() }
    }

    suspend fun refreshBereiche() {
        bereiche = loadBereiche().sortedBy { it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }.lowercase() }
    }

    LaunchedEffect(art, currentFirmenId(), userVersion) {
        refreshUsers()
        if (!isNet(art)) {
            refreshRollen()
            refreshBereiche()
        }
    }

    val tableHeaders = userTableHeaders(art)
    val dropdownOptions = if (isNet(art)) {
        mapOf(
            "Position" to listOf("Fahrer", "Verwaltung"),
            "Sprache" to LocalizationManager.languages.map { it.name }
        )
    } else {
        mapOf(
            "Sprache" to LocalizationManager.languages.map { it.name },
            "Rolle" to (listOf("") + rollenMitFahrer(rollen).map { it.name.ifBlank { it.id } }),
            "Bereich" to (listOf("") + bereiche.map { it.name.ifBlank { it.bezeichnung.ifBlank { it.id } } })
        )
    }

    TabularScreen(
        screenKey = "UserVerwaltungTabelle",
        rollenKey = "UserVerwaltung",
        title = "Benutzer Tabelle",
        headers = tableHeaders,
        data = users.map { it.id to userToTableRow(it, rollen, bereiche, art) },
        models = users,
        editable = true,
        csvImportAccept = ".csv,.tsv,.txt",
        toggleColumns = USER_TABLE_TOGGLE_HEADERS,
        nonEditableCols = setOf("ID", "Datenschutz akzeptiert", "Passwort geändert"),
        dropdownOptions = dropdownOptions,
        createButtonLabel = "Benutzer anlegen",
        saveButtonLabel = "Benutzer speichern",
        importButtonLabel = "CSV importieren",
        exportFileName = "usertabelle.csv",
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                users = sortUsers(searchUsers(art, filters))
            }
        },
        createRow = {
            if (isNet(art)) {
                listOf(it, "", "", "", "", "Verwaltung", sprachLabel("de"), "✅", "⬜", "⬜", "⬜")
            } else listOf(
                it,
                "",
                "",
                "",
                "",
                "",
                "",
                sprachLabel("de"),
                "✅",
                "✅",
                "",
                "",
                "",
                "",
                "⬜",
                "⬜"
            )
        },
        onCellEditTransform = { _, column, _, row ->
            userTableRowWithAutoKuerzel(row, art, column)
        },
        onCommit = { rows ->
            scope.launch {
                val existingById = users.associateBy { user -> user.id }
                var hadError = false
                rows.forEach { (id, cols) ->
                    val existing = existingById[id]
                    val state = tableRowToUserState(cols, rollen, bereiche, art, existing)
                    val saved = persistState(art, state)
                    if (saved == null) hadError = true
                }
                refreshUsers()
                if (hadError) {
                    AppDialogs.alert("Mindestens ein Benutzer konnte nicht gespeichert werden.")
                }
            }
        },
        onDeleteRows = { ids ->
            var hadError = false
            ids.forEach { id ->
                if (!deleteUserEntry(art, id)) hadError = true
            }
            refreshUsers()
            if (hadError) {
                AppDialogs.alert("Mindestens ein Benutzer konnte nicht gelöscht werden.")
            }
        },
        onResetPassword = { ids ->
            scope.launch {
                var hadError = false
                ids.forEach { id ->
                    if (resetUserPassword(art, id) == null) hadError = true
                }
                refreshUsers()
                if (hadError) {
                    AppDialogs.alert("Mindestens ein Passwort konnte nicht zurückgesetzt werden.")
                }
            }
        }
    )
}
